import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import { z } from 'zod'
import { ContractAPI } from '@contracts'
import { tsRestClient } from '@/utils/api'

// Infer types from ContractAPI
export type ListCouponsRequest = z.infer<typeof ContractAPI.coupons.listCoupons.query>
export type CreateCouponRequest = z.infer<typeof ContractAPI.coupons.createCoupon.body>
export type UpdateCouponStatusRequest = z.infer<typeof ContractAPI.coupons.updateCoupon.body>

/**
 * Hook to fetch a list of coupons with optional filters
 */
export const useListCoupons = (filters?: ListCouponsRequest, options?: { enabled?: boolean }) => {
	const queryKey = ['coupons', 'list', filters || {}]
	return tsRestClient.coupons.listCoupons.useQuery(
		queryKey,
		{ query: filters || {} },
		{ ...options, queryKey },
	)
}

/**
 * Hook to fetch a single coupon by ID
 */
export const useGetCoupon = (couponId: number, options?: { enabled?: boolean }) => {
	const queryKey = ['coupons', 'detail', couponId]
	return tsRestClient.coupons.getCoupon.useQuery(
		queryKey,
		{ params: { couponId } },
		{ ...options, queryKey },
	)
}

/**
 * Hook to create a new coupon
 */
export const useCreateCoupon = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.createCoupon.useMutation({
		onSuccess: () => {
			message.success('クーポンが作成されました')
			queryClient.invalidateQueries({
				queryKey: ['coupons'],
			})
		},
	})
}

/**
 * Hook to update coupon status
 */
export const useUpdateCouponStatus = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.updateCoupon.useMutation({
		onSuccess: () => {
			message.success('クーポンステータスが更新されました。')
			queryClient.invalidateQueries({
				queryKey: ['coupons'],
			})
		},
	})
}

/**
 * Hook to batch create coupons
 */
export const useBatchCreateCoupons = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.bulkCreateCoupons.useMutation({
		onSuccess: (data) => {
			if (data.status === 200) {
				message.success(data.body.message || 'クーポンが一括作成されました')
				queryClient.invalidateQueries({
					queryKey: ['coupons'],
				})
			} else {
				message.error('クーポンの作成に失敗しました')
			}
		},
		onError: () => {
			message.error('クーポンの作成に失敗しました')
		},
	})
}

/**
 * Hook to list coupon masters
 */
export const useListCouponMasters = (options?: {
	enabled?: boolean
	includeInactive?: boolean
}) => {
	const queryKey = ['coupon-masters', 'list', options?.includeInactive ?? false]
	return tsRestClient.coupons.masters.listCouponMasters.useQuery(
		queryKey,
		{ query: { includeInactive: String(options?.includeInactive ?? false) } },
		{ enabled: options?.enabled ?? true, queryKey },
	)
}

/**
 * Hook to create a coupon master
 */
export const useCreateCouponMaster = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.masters.createCouponMaster.useMutation({
		onSuccess: () => {
			message.success('クーポンマスターが作成されました')
			queryClient.invalidateQueries({ queryKey: ['coupon-masters'] })
		},
	})
}

/**
 * Hook to update a coupon master
 */
export const useUpdateCouponMaster = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.masters.updateCouponMaster.useMutation({
		onSuccess: () => {
			message.success('クーポンマスターが更新されました')
			queryClient.invalidateQueries({ queryKey: ['coupon-masters'] })
		},
	})
}

/**
 * Hook to delete a coupon master
 */
export const useDeleteCouponMaster = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.masters.deleteCouponMaster.useMutation({
		onSuccess: () => {
			message.success('クーポンマスターが削除されました')
			queryClient.invalidateQueries({ queryKey: ['coupon-masters'] })
		},
	})
}

/**
 * Hook to list coupon usage history
 */
export const useListCouponUsageHistory = (
	filters?: z.infer<typeof ContractAPI.coupons.listCouponUsageHistory.query>,
	options?: { enabled?: boolean },
) => {
	const queryKey = ['coupon-usage-history', 'list', filters || {}]
	return tsRestClient.coupons.listCouponUsageHistory.useQuery(
		queryKey,
		{ query: filters || {} },
		{ ...options, queryKey },
	)
}

/**
 * Hook to delete a coupon usage history record
 */
export const useDeleteCouponUsageHistory = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.deleteCouponUsageHistory.useMutation({
		onSuccess: () => {
			message.success('使用履歴を削除しました')
			queryClient.invalidateQueries({ queryKey: ['coupons'] })
			queryClient.invalidateQueries({ queryKey: ['coupon-usage-history'] })
		},
	})
}

/**
 * Hook to create a coupon usage history record
 */
export const useCreateCouponUsageHistory = () => {
	const queryClient = useQueryClient()
	return tsRestClient.coupons.createCouponUsageHistory.useMutation({
		onSuccess: () => {
			message.success('使用履歴を追加しました')
			queryClient.invalidateQueries({ queryKey: ['coupons'] })
			queryClient.invalidateQueries({ queryKey: ['coupon-usage-history'] })
		},
	})
}
